import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import uniqueId from 'lodash.uniqueid';
import {
    paths,
    userInfoRowContainerId,
    userInfoUpdateSecurityQuestionId,
    userInfoSecurityQuestionAlertWarning,
} from '../../../constants';
import {
    StyledNavIcon,
    StyledAlert,
    AccentButton,
} from '../../../styles/common';
import { AppStateContext } from '../../../context/AppContext';

const InfoContainer = styled(Container)`
    text-align: left;
`;
const UserInfoRow = styled(Row)`
    padding-top: 5px;
    padding-bottom: 5px;
`;

const SecurityInfoRows = ({ security }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const question = security.find(element => element.type === 'securityQuestion');
    if (!question) {
        return null;
    }
    return (
        <InfoContainer data-test-id={userInfoRowContainerId}>
            <Row>
                <Col>
                    <AccentButton
                        outline
                        color="primary"
                        size="sm"
                        data-test-id={userInfoUpdateSecurityQuestionId}
                        onClick={() => navigate(paths.updateSecurity)}
                    >
                        <StyledNavIcon icon="edit" />
                        {t('userInfo$securityQuestionTitle')}
                    </AccentButton>
                </Col>
            </Row>
            <Row>
                <Col>
                    <StyledAlert
                        color={question.enrolled ? 'info' : 'warning'}
                        data-test-id={userInfoSecurityQuestionAlertWarning}
                    >
                        {question.enrolled
                            ? t('userInfo$securityQuestionCompleted')
                            : t('userInfo$securityQuestionPending')}
                    </StyledAlert>
                </Col>
            </Row>
        </InfoContainer>
    );
};

SecurityInfoRows.propTypes = {
    security: PropTypes.array.isRequired,
};

const UserInfoRows = ({ user }) => {
    const { t } = useTranslation();
    const { dynamicRole } = useContext(AppStateContext);

    if (!user) {
        return null;
    }

    const attributes = {};
    attributes.fullName =
        [user.firstName, user.lastName].filter((name) => name).join(' ') || '';
    ['username', 'email', 'groups'].forEach((attr) => {
        attributes[attr] = user[attr] || '';
    });

    // Show only dynamic role from context (NOT static roles)
    if (dynamicRole) {
        attributes.role = dynamicRole;
    }

    const displayNameMap = {};

    user.customAttributes.forEach((attr) => {
        attributes[attr.key] = attr.value;
        if (attr.displayName) {
            displayNameMap[attr.key] = attr.displayName;
        }
    });

    return (
        <InfoContainer data-test-id={userInfoRowContainerId}>
            {Object.keys(attributes).map((attr) => {
                let value;
                if (attributes[attr] instanceof Array) {
                    value = (
                        <Col>
                            {attributes[attr].map((value) => (
                                <Row key={value}>{value}</Row>
                            ))}
                        </Col>
                    );
                } else {
                    value = (
                        <Col>
                            <Row>{attributes[attr]}</Row>
                        </Col>
                    );
                }
                return (
                    <UserInfoRow key={uniqueId('userInfoRow_')}>
                        <Col xs="2">
                            {`${
                                displayNameMap[attr] ||
                                t(`userInfo$attributes$${attr}`) ||
                                attr.charAt(0).toUpperCase() + attr.substring(1)
                            }:`}
                        </Col>
                        {value}
                    </UserInfoRow>
                );
            })}
        </InfoContainer>
    );
};

UserInfoRows.propTypes = {
    user: PropTypes.object.isRequired,
};

const UserInfoWithData = ({ user, security }) => {
    if (user !== undefined) {
        return <UserInfoRows user={user} />;
    }
    if (security !== undefined) {
        return <SecurityInfoRows security={security} />;
    }
    return null;
};

UserInfoWithData.propTypes = {
    user: PropTypes.object,
    security: PropTypes.array,
};

UserInfoWithData.defaultProps = {
    user: undefined,
    security: undefined,
};

export default UserInfoWithData;
